import { Button, Center, createStyles, Loader } from "@mantine/core";
import React, { useEffect, useState } from "react";
import swal from "sweetalert";

import {
  getUpdatedPackage,
  updatePriceIdOnStripe,
} from "../../services/package";
import { useNavigate } from "react-router-dom";
import { showSuccess } from "../../utils";

const useStyles = createStyles(() => ({
  container: {
    backgroundColor: "#f8f9fa",
    minHeight: "100vh",
    padding: "20px 0",
    display: "flex",
    justifyContent: "center",
    alignItems: "flex-start",
    fontFamily: "Arial, sans-serif",
  },
  card: {
    width: "520px",
    maxWidth: "100%",
    backgroundColor: "#ffffff",
    borderRadius: "8px",
    overflow: "hidden",
    boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
  },
  header: {
    backgroundColor: "#FFDB81",
    padding: "14px 20px",
    color: "#000",
    fontSize: "18px",
    fontWeight: "bold",
  },
  title: {
    padding: "20px 25px",
    fontSize: "16px",
    fontWeight: "bold",
    color: "#111827",
  },
  subtitle: {
    padding: "0 25px 15px 25px",
    fontSize: "14px",
    color: "#374151",
  },
  accountBox: {
    padding: "0 25px 20px 25px",
  },
  accountTable: {
    width: "100%",
    border: "1px solid #e5e7eb",
    borderRadius: "8px",
    fontSize: "14px",
    color: "#374151",
    borderCollapse: "separate",
    borderSpacing: "0",
  },
  accountRow: {
    "& td": {
      padding: "6px",
    },
  },
  accountLabel: {
    fontWeight: "bold",
    width: "140px",
  },
  planDetailsTitle: {
    padding: "10px 25px",
    fontSize: "15px",
    fontWeight: "bold",
    color: "#111827",
  },
  planDetailsBox: {
    padding: "0 25px 20px 25px",
  },
  planTable: {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: "14px",
    color: "#374151",
    border: "1px solid #e5e7eb",
  },
  planTableHeader: {
    background: "#f9fafb",
    fontWeight: "bold",
    "& td": {
      padding: "8px",
    },
  },
  planTableRow: {
    "& td": {
      padding: "8px",
      borderTop: "1px solid #e5e7eb",
    },
  },
  planTableRowAlt: {
    background: "#f9fafb",
    "& td": {
      padding: "8px",
      borderTop: "1px solid #e5e7eb",
    },
  },
  highlighted: {
    color: "#2563eb",
    fontWeight: "bold",
  },
  buttonContainer: {
    textAlign: "center",
    padding: "20px",
  },
  button: {
    display: "inline-block",
    padding: "12px 32px",
    backgroundColor: "#FFDB81",
    color: "#000",
    fontSize: "14px",
    fontWeight: "bold",
    textDecoration: "none",
    borderRadius: "6px",
    border: "none",
    cursor: "pointer",
    transition: "background-color 0.2s",
    "&:hover": {
      backgroundColor: "#ffd45c",
    },
  },
}));

export const UpdatePlan = () => {
  const { classes } = useStyles();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [updatedPackage, setUpdatedPackage] = useState(null);
  const params = new URLSearchParams(window.location.search);
  const actualAmount = params.get("actualAmount");
  const updatedPackageId = params.get("updatedPackageId");

  const handleConfirm = async () => {
    try {
      setLoading(true); // start loading
      const payload = {
        updatedPackageId,
      };
      const res = await updatePriceIdOnStripe(payload);
      if (res.status === 200) {
        showSuccess("Plan Updated Successfully");
        navigate("/home");
      }
    } catch (err) {
      console.error("Error upgrading plan:", err);
    } finally {
      setLoading(false); // stop loading
    }
  };

  useEffect(() => {
    const getUpdatedPackageData = async () => {
      try {
        setLoading(true); // start loading before API call

        const res = await getUpdatedPackage(updatedPackageId, false);
        if (res?.data?.updatedPackage === null) {
          swal({
            title: "Email link expired",
            text: "Please contact to admin for updating your plan.",
            icon: "warning",
            button: "OK",
          }).then(() => {
            navigate("/home");
          });
        }
        setUpdatedPackage(res?.data);
      } catch (error) {
        console.error("Error fetching updated package:", error);
      } finally {
        setLoading(false); // stop loading when done
      }
    };

    getUpdatedPackageData();
  }, [updatedPackageId]);

  // Sample data - replace with your actual data
  const subscription = {
    businessId: {
      companyName: updatedPackage?.subscription
        ? updatedPackage?.subscription?.businessId?.companyName
        : "-" || "-",
    },
  };

  const oldPackage = {
    staff: updatedPackage?.updatedPackage?.originalPackageId?.staff || "-",
    patient: updatedPackage?.updatedPackage?.originalPackageId?.patient || "-",
    facility:
      updatedPackage?.updatedPackage?.originalPackageId?.facility || "-",
    cost: updatedPackage?.updatedPackage?.originalPackageId?.cost || "-",
  };

  const body = {
    staff: updatedPackage?.updatedPackage?.staff || "-",
    patient: updatedPackage?.updatedPackage?.patient || "-",
    facility: updatedPackage?.updatedPackage?.facility || "-",
    newCost: updatedPackage?.updatedPackage?.cost || "-",
  };

  const formattedDate = new Date(
    updatedPackage?.subscription ? updatedPackage?.subscription.expiredAt : null
  ).toLocaleDateString("en-US", {
    month: "short", // "Sep"
    day: "numeric", // "1"
    year: "numeric", // "2025"
  });

  if (loading && !updatedPackage) {
    return (
      <Center style={{ minHeight: "100vh" }}>
        <Loader color="yellow" size="lg" />
      </Center>
    );
  }

  return (
    <div className={classes.container}>
      <div className={classes.card}>
        {/* Header */}
        <div className={classes.header}>Ogiso</div>

        {/* Title */}
        <div className={classes.title}>Plan {updatedPackage?.changeType === "upgrade" ? "Upgrade" : "Downgrade"} review</div>

        {/* Subtitle */}
        <div className={classes.subtitle}>Please review the changes below.</div>

        {/* Account Box */}
        <div className={classes.accountBox}>
          <table className={classes.accountTable}>
            <tbody>
              <tr className={classes.accountRow}>
                <td className={classes.accountLabel}>Account</td>
                <td>{subscription?.businessId?.companyName}</td>
              </tr>
              <tr className={classes.accountRow}>
                <td className={classes.accountLabel}>Change</td>
                <td>
                  Clients: {oldPackage.patient} → {body.patient}
                </td>
              </tr>
              <tr className={classes.accountRow}>
                <td className={classes.accountLabel}>Monthly price</td>
                <td>
                  ${oldPackage.cost} → ${body.newCost}
                </td>
              </tr>
              {updatedPackage?.changeType === "upgrade" && (
                <>
                  <tr className={classes.accountRow}>
                    <td className={classes.accountLabel}>Next billing date</td>
                    <td>{formattedDate}</td>
                  </tr>
                  <tr className={classes.accountRow}>
                    <td className={classes.accountLabel}>
                      Due today on confirm
                    </td>
                    <td>${actualAmount} (prorated)</td>
                  </tr>
                </>
              )}
            </tbody>
          </table>
        </div>

        {/* Plan Details Title */}
        <div className={classes.planDetailsTitle}>Plan details</div>

        {/* Plan Details Table */}
        <div className={classes.planDetailsBox}>
          <table className={classes.planTable}>
            <thead>
              <tr className={classes.planTableHeader}>
                <td>Item</td>
                <td>Previous</td>
                <td>New</td>
              </tr>
            </thead>
            <tbody>
              <tr className={classes.planTableRow}>
                <td>Staff</td>
                <td>{oldPackage.staff}</td>
                <td>{body.staff}</td>
              </tr>
              <tr className={classes.planTableRowAlt}>
                <td>Clients</td>
                <td>{oldPackage.patient}</td>
                <td className={classes.highlighted}>{body.patient}</td>
              </tr>
              <tr className={classes.planTableRow}>
                <td>Facilities</td>
                <td>{oldPackage.facility}</td>
                <td>{body.facility}</td>
              </tr>
              <tr className={classes.planTableRowAlt}>
                <td>Monthly price</td>
                <td>${oldPackage.cost}</td>
                <td className={classes.highlighted}>${body.newCost}</td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Button */}
        <div className={classes.buttonContainer}>
          <Button
            title="Confirm"
            color="yellow"
            loading={loading}
            onClick={() => {
              handleConfirm();
            }}
          >
            {loading ? "Confirming..." : "Confirm Now"}
          </Button>
        </div>
      </div>
    </div>
  );
};
